import { useState, useEffect, useMemo } from 'react'

import { Language } from '~/utils/language'
import { useLanguage } from '~/context/language'
import LanguageTab from '~/components/LanguageTab'
import Loading from '~/components/Loading'

import MpayLogoImage from './images/mpay_logo.png'
import Step1Image from './images/step_1.png'
import Step2Image from './images/step_2.png'
import Step3Image from './images/step_3.png'
import styles from './MpayDownloadPage.module.css'

type Step = { hint: string; text: string; image: string }

type LanguageSetting = {
  language: Language
  name: string
  title: string
  steps: Step[]
}

const LANGUAGE_SETTING_LIST: LanguageSetting[] = [
  {
    language: Language.ZhHK,
    name: '中文',
    title: '使用MPay APP租借移動電源',
    steps: [
      {
        hint: '第一步',
        text: '打開MPay APP點擊【共享充電寶】',
        image: Step1Image,
      },
      {
        hint: '第二步',
        text: '點擊【ChargeSPOT】進入小程序',
        image: Step2Image,
      },
      {
        hint: '第三步',
        text: '點擊【掃碼租借移動電源】掃描二維碼即可租借移動電源',
        image: Step3Image,
      },
    ],
  },
  {
    language: Language.EnUS,
    name: 'EN',
    title: 'Rent power bank by Mpay APP',
    steps: [
      {
        hint: 'Step 1',
        text: 'Open Mpay APP and tap 【Power bank】',
        image: Step1Image,
      },
      {
        hint: 'Step 2',
        text: 'Tap【ChargeSPOT】to open mini program',
        image: Step2Image,
      },
      {
        hint: 'Step 3',
        text: 'Tap 【Scan to Rent】and scan QR to rent power bank',
        image: Step3Image,
      },
    ],
  },
]

function MpayDownloadPage() {
  const currentLanguage = useLanguage()
  // HACK: fallback
  const defaultLanguage =
    currentLanguage === Language.JaJP ? Language.EnUS : currentLanguage
  const [language, setLanguage] = useState<Language>(defaultLanguage)

  // Auto Redirect
  const { type, uuid } = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search)
    return {
      type: searchParams.get('type'),
      uuid: searchParams.get('uuid'),
    }
  }, [])
  const isAutoRedirect = Boolean(uuid)
  useEffect(() => {
    if (!uuid || !type) return
    const url = new URL(
      // TODO: fix http here
      window.CHARGESPOT_APP_ENV === 'prod'
        ? 'https://mpay.charge-spot.com'
        : 'https://mpay-pre.charge-spot.com'
    )
    // TODO: change "type" to "from"
    url.searchParams.set('type', 'lend')
    url.searchParams.set('battery_type', type || '')
    url.searchParams.set('uuid', uuid || '')
    window.location.href = url.toString()
  }, [type, uuid])

  const languageSetting = LANGUAGE_SETTING_LIST.find(
    (s) => s.language === language
  )
  if (!languageSetting) return null
  const { title, steps } = languageSetting

  if (isAutoRedirect) {
    return (
      <div className={styles.root}>
        <Loading />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <LanguageTab
        className={styles.languageTab}
        languageSettingList={LANGUAGE_SETTING_LIST}
        currentLanguage={language}
        onChange={setLanguage}
      />

      <img className={styles.logo} src={MpayLogoImage} alt="MPay logo" />
      <div className={styles.title}>{title}</div>

      <div className={styles.stepContainer}>
        {steps.map((step, index) => (
          <div key={index} className={styles.step}>
            <div className={styles.stepHeader}>
              <div className={styles.stepHint}>{step.hint}</div>
              <div className={styles.stepText}>{step.text}</div>
            </div>
            <img className={styles.stepImage} src={step.image} alt="" />
          </div>
        ))}
      </div>
    </div>
  )
}

export default MpayDownloadPage
